import {TaskStatus, defaultInputParams, imageFormatConfig} from '@/views/V2/image/imageAgent/utils/constants';
import {calculateCardSize} from '@/views/V2/image/imageAgent/utils/cardHeightCalculator';
import {format, isSameDay, isYesterday} from 'date-fns';

// 轮询常量配置
const POLLING_CONFIG = {
    TASK_STATUS_INTERVAL: 1500, // 任务状态轮询间隔（毫秒）
    CUTOUT_TASK_INTERVAL: 1500, // 抠图任务轮询间隔（毫秒）
    MAX_CUTOUT_RETRIES: 60 // 抠图任务最大重试次数
};

export default {
    namespaced: true,
    state: {
        // 参数配置
        params: {...defaultInputParams},
        // 详情输入框配置
        modelParams: {...defaultInputParams},
        isInputExpanded: false,
        inputLoading: false,
        // 任务列表管理状态
        taskList: [],
        loading: false,
        offset: 0,
        pageSize: 20,
        hasMore: true,
        total: 0,
        pollingTimer: null,
        currentTaskId: null,
        lastSubmitTime: 0,
        pageLoadingStatus: {show: false, title: ''},
        // 详情弹窗状态管理
        detailDialogOpen: false,
        // 详情页完整数据缓存（包含所有需要的信息）
        detailTaskInfo: null,
        detailRecordInfo: null,
        // 滚动方向（包含方向和时间戳，确保每次都能触发 watch）
        scrollDirection: {direction: 'down', timestamp: Date.now()},
        // 轮询内部状态（不再暴露 generateStatus/taskId）
        _pollingPromises: {}, // { [taskId]: Promise }
        _shouldStopPollingMap: {}, // { [taskId]: boolean }
        // 提交中标志（用于区分 submitting 状态）
        _isSubmitting: false,
        // 任务进度记录（taskId -> progress）
        taskProgressMap: {}
    },
    mutations: {
        // 通用状态更新
        UPDATE_COMMON_STATE(state, {key, value}) {
            state[key] = value;
        },
        UPDATE_MULTIPLE_STATES(state, updates) {
            for (let update of updates) {
                state[update.key] = update.value;
            }
        },
        // 任务列表管理 mutations（有特殊逻辑的保留）
        REMOVE_TASK(state, taskId) {
            const index = state?.taskList?.findIndex(t => t.taskId === taskId);
            if (index !== -1) {
                state.taskList.splice(index, 1);
            }
            // 清理该任务的进度记录
            delete state.taskProgressMap[taskId];
        },
        REMOVE_IMAGE(state, {taskId, recordId}) {
            const task = state?.taskList?.find(t => t.taskId === taskId);
            if (task) {
                task.records = task.records.filter(img => img.recordId !== recordId);
            }
        },
        REPLACE_TASK_LIST(state, taskList) {
            state.taskList = taskList;
        },
        ADD_TASK(state, task) {
            state.taskList.unshift(task);
            // 重新计算前 2 个任务的日期标签（新插入的 + 原第一个）
            // 确保日期标签正确显示（只有第一个"今天"任务显示标签）
            recalcDateLabels(state.taskList, 0, Math.min(2, state.taskList.length));
        },
        UPDATE_TASK(state, {taskId, updates}) {
            const index = state?.taskList?.findIndex(t => t.taskId === taskId);
            if (index !== -1) {
                // 使用 splice 替换数组元素，触发响应式更新
                // 合并旧对象和新的 updates，保留未更新的字段
                const updatedTask = {...state.taskList[index], ...updates};
                state.taskList.splice(index, 1, updatedTask);
            }
        },
        UPDATE_TASK_PROGRESS(state, {taskId, progress}) {
            state.taskProgressMap = {
                ...state.taskProgressMap,
                [taskId]: progress
            };
        },
        CLEAR_TASK_PROGRESS(state, taskId) {
            delete state.taskProgressMap[taskId];
        }
    },
    actions: {
        updateCommonState({commit}, {key, value}) {
            commit('UPDATE_COMMON_STATE', {key, value});
        },
        updateMultipleState({commit}, data) {
            commit('UPDATE_MULTIPLE_STATES', data);
        },

        // 加载任务列表（分页）
        async fetchTaskList({commit, state}, {model, refresh = false}) {
            commit('UPDATE_COMMON_STATE', {key: 'loading', value: true});

            if (refresh) {
                commit('UPDATE_COMMON_STATE', {key: 'offset', value: 0});
            }

            // 构造请求参数
            const params = {
                offset: state.offset,
                pageSize: state.pageSize,
                taskType: [33, 34, 35], // 31=抠图, 32=扩图, 33=AGENT图片生成, 34=AGENT抠图, 35=AGENT扩图 18, 19, 31, 32,
                sortBy: 'add_time',
                taskStatus: Object.values(TaskStatus),
                sortOrder: 'desc' // 降序,最新→最旧（便于分页）
            };

            try {
                // 调用真实接口
                const data = await model.imageCreate.getHistory(params);

                // 数据转换：将返回的数据转换为任务卡片需要的格式
                // ⚠️ 关键：使用 listData 而不是 list，listData 包含所有状态的任务
                let newTasks = transformTaskData(data.listData || []);

                // ✅ 提前计算去重后的任务（无论 refresh 还是 loadMore 都需要）
                const existingTaskIds = refresh ? new Set() : new Set(state.taskList.map(t => t.taskId));
                const actualNewTasks = newTasks.filter(t => !existingTaskIds.has(t.taskId));

                if (refresh) {
                    // 刷新：替换整个列表（加载最新的数据）
                    commit('REPLACE_TASK_LIST', actualNewTasks);
                }
                else {
                    // 加载更多：将更旧的数据追加到列表尾部
                    // transformTaskData 不再反转，接口返回的更旧数据直接追加到末尾
                    state.taskList = [...state.taskList, ...actualNewTasks];
                    // 重新计算连接点及之后的日期标签
                    // 因为旧列表最后一个任务的"下一个任务"变了，需要重新判断是否显示标签
                    const oldLen = state.taskList.length - actualNewTasks.length;
                    const startIndex = Math.max(0, oldLen - 1); // 从连接点前一个任务开始
                    recalcDateLabels(state.taskList, startIndex, state.taskList.length);
                }

                // 更新 offset
                // ✅ 基于实际合并的任务数（去重后），而不是接口返回的全部任务数
                const actualLoaded = actualNewTasks.length;
                const newOffset = state.offset + actualLoaded;
                const totalCount = data.totalCount || 0;
                commit('UPDATE_MULTIPLE_STATES', [
                    {key: 'offset', value: newOffset},
                    {key: 'hasMore', value: newOffset < totalCount},
                    {key: 'total', value: totalCount}
                ]);
            }
            catch (error) {
                console.error('加载任务列表失败:', error);
                throw error;
            }
            finally {
                commit('UPDATE_COMMON_STATE', {key: 'loading', value: false});
            }
        },

        // 初始化时检查并恢复轮询
        async resumePollingIfNeeded({dispatch, state}, {model}) {
            // 检查 taskList 中所有进行中的主任务（status === 1 && AGENT_IMAGE_GENERATE）
            const generatingTasks = state.taskList.filter(
                t => t.status === 1 && t.imageGenerateType === 'AGENT_IMAGE_GENERATE'
            );
            for (const task of generatingTasks) {
                console.log('发现进行中的任务，恢复轮询:', task.taskId);
                dispatch('startPolling', {model, taskId: task.taskId}).catch(error => {
                    console.error('恢复轮询失败:', error);
                });
            }
        },

        // 删除单个图片
        async deleteImage({commit, state, dispatch}, {model, taskId, recordId}) {
            const task = state.taskList.find(t => t.taskId === taskId);
            if (!task) {
                return;
            }

            // 获取图片的recordId
            const image = task.records.find(img => img.recordId === recordId);
            if (!image) {
                return;
            }

            // 调用后端 API 删除图片记录（和一键出图保持一致）
            try {
                await model.imageCreate.deleteCutoutRecord({recordIds: [recordId]});
            }
            catch (error) {
                console.error('删除图片失败:', error);
                throw error;
            }

            // 从任务的 records 数组中移除该图片
            commit('REMOVE_IMAGE', {taskId, recordId});

            // 如果任务中没有图片了，删除整个任务
            const updatedTask = state.taskList.find(t => t.taskId === taskId);
            if (updatedTask && updatedTask.records.length === 0) {
                await dispatch('deleteTask', {taskId});
            }
        },

        // 删除任务（当任务中没有图片时调用）
        async deleteTask({commit, state}, taskId) {
            try {
                // 后端接口会自动删除任务（因为已经没有图片记录了）
                // 这里暂时不需要调用接口，删除图片时会自动删除任务
                // await model.imageCreate.delTaskRecords({records: [{taskId}]});

                // 从列表中移除
                commit('REMOVE_TASK', taskId);

                // 删除任务后，offset 需要减 1
                const newOffset = Math.max(0, state.offset - 1);
                commit('UPDATE_COMMON_STATE', {key: 'offset', value: newOffset});
            }
            catch (error) {
                console.error('删除任务失败:', error);
                throw error;
            }
        },

        async submitTask({commit, state, dispatch}, {model, paramsKey = 'params', directParams = null}) {
            // 如果传入了 directParams，直接使用；否则从 state 中获取
            const params = directParams || state[paramsKey];

            // 设置提交中标志
            state._isSubmitting = true;

            try {
                // 构建提交参数
                const submitParams = buildSubmitParams(params);

                const result = await model.imageCreate.submitImageAgentTask(submitParams);

                // 检查是否是敏感图片错误
                if (result?.success === false && result?.isSensitiveImageError) {
                    // 清除提交中标志
                    state._isSubmitting = false;
                    // 处理敏感错误（错误码 7003）
                    const message = result.message || '';
                    const match = message.match(/风控图片序号:\s*\[(.+?)\]/);
                    // 判断是图片敏感还是内容敏感
                    if (match) {
                        // 情况1：图片敏感 - 消息中包含"风控图片序号"
                        const indexesStr = match[1];
                        // 解析序号数组，如 "1, 3" -> [1, 3]
                        const sensitiveIndexes = indexesStr.split(',').map(s => {
                            return parseInt(s.trim(), 10);
                        });
                        if (sensitiveIndexes.length > 0) {
                            // 生成提示文案，如 "图片1和图片3"
                            const imageNames = sensitiveIndexes.map(idx => `图片${idx}`).join('和');
                            // 从 refImageList 中删除对应的图片（注意序号从1开始，数组下标从0开始）
                            const newRefImageList = params.refImageList.filter((_, index) => {
                                return !sensitiveIndexes.includes(index + 1);
                            });
                            // 更新参数
                            commit('UPDATE_COMMON_STATE', {
                                key: paramsKey,
                                value: {
                                    ...params,
                                    refImageList: newRefImageList
                                }
                            });
                            // 抛出自定义错误
                            const customError = new Error(`${imageNames}不合规已被清空，请更换参考图`);
                            customError.isSensitiveImageError = true;
                            customError.sensitiveIndexes = sensitiveIndexes;
                            throw customError;
                        }
                    }
                    else {
                        // 情况2：内容敏感 - 消息中没有"风控图片序号"
                        // 清空 prompt 和 tiptapContent
                        commit('UPDATE_COMMON_STATE', {
                            key: paramsKey,
                            value: {
                                ...params,
                                prompt: '',
                                tiptapContent: defaultInputParams.tiptapContent
                            }
                        });
                        // 抛出错误，直接使用后端返回的 message
                        const customError = new Error(message || '内容不合规，请修改后重试');
                        customError.isSensitiveContentError = true;
                        throw customError;
                    }
                }

                // 正常流程：获取 taskId
                const taskId = result.data?.taskId;
                if (!taskId) {
                    throw new Error('未获取到 taskId');
                }
                // 构建参考图片列表
                const referenceImages = buildReferenceImages(params);
                // 创建新任务对象
                const newTask = createNewTask(taskId, params, referenceImages);
                // 只通过 ADD_TASK 添加到列表，generateStatus 通过 getter 自动推导
                commit('ADD_TASK', newTask);
                // 清空输入参数
                commit('UPDATE_COMMON_STATE', {key: 'params', value: {...defaultInputParams}});
                // 清除提交中标志
                state._isSubmitting = false;
                // 启动轮询，传入明确的 taskId
                dispatch('startPolling', {model, taskId});
            }
            catch (error) {
                console.error('提交任务失败:', error);
                // 出错时也清除提交中标志
                state._isSubmitting = false;
                throw error;
            }
        },

        async startPolling({commit, state, dispatch}, {model, taskId}) {
            // 如果传入了 taskId，使用传入的；否则查找生成中的主任务（AGENT_IMAGE_GENERATE）
            let currentTaskId = taskId;
            if (!currentTaskId) {
                const generatingTask = state.taskList?.find(
                    t => t?.status === 1 && t?.imageGenerateType === 'AGENT_IMAGE_GENERATE'
                );
                if (!generatingTask) {
                    throw new Error('没有找到生成中的任务');
                }
                currentTaskId = generatingTask.taskId;
            }
            console.log('开始轮询任务:', currentTaskId);

            // 重置该任务的停止标志
            state._shouldStopPollingMap[currentTaskId] = false;

            try {
                // 使用 rollImageResult 进行轮询
                state._pollingPromises[currentTaskId] = model.imageCreate.rollImageResult(currentTaskId, res => {
                    // 检查是否需要停止该任务的轮询
                    if (state._shouldStopPollingMap[currentTaskId]) {
                        throw new Error('轮询已取消');
                    }
                    console.log('轮询进度:', res);
                    // 实时更新 taskList 中的任务数据
                    dispatch('updateTaskInList', {taskData: res, taskId: currentTaskId});
                });

                const result = await state._pollingPromises[currentTaskId];
                console.log('生成完成:', result);

                // 先用轮询结果快速更新，让用户立即看到结果
                dispatch('updateTaskInList', {taskData: result, taskId: currentTaskId});

                delete state._pollingPromises[currentTaskId];
                delete state._shouldStopPollingMap[currentTaskId];

                // 异步获取完整数据（补充 queryTime、imageGenerateType 等字段）
                model.imageCreate.getHistory({
                    offset: 0,
                    pageSize: 1,
                    taskId: currentTaskId
                }).then(historyResult => {
                    if (historyResult.listData && historyResult.listData.length > 0) {
                        dispatch('updateTaskInList', {taskData: historyResult.listData[0], taskId: currentTaskId});
                    }
                }).catch(error => {
                    console.error('获取任务完整数据失败:', error);
                });
            }
            catch (error) {
                // 如果是取消操作，不算错误
                if (error?.message?.includes('轮询已取消')) {
                    console.log('轮询已被用户取消');
                    delete state._pollingPromises[currentTaskId];
                    delete state._shouldStopPollingMap[currentTaskId];
                    return;
                }

                console.error('轮询任务结果失败:', error);
                // 轮询失败时，标记当前轮询的任务状态为失败（使用 currentTaskId 精确匹配）
                commit('UPDATE_TASK', {
                    taskId: currentTaskId,
                    updates: {status: 3, taskStatus: 3} // 3 表示失败
                });
                delete state._pollingPromises[currentTaskId];
                delete state._shouldStopPollingMap[currentTaskId];
                throw error;
            }
        },

        stopPolling({state}, {taskId} = {}) {
            if (taskId) {
                // 停止指定任务的轮询
                state._shouldStopPollingMap[taskId] = true;
            }
            else {
                // 停止所有任务的轮询
                Object.keys(state._shouldStopPollingMap).forEach(id => {
                    state._shouldStopPollingMap[id] = true;
                });
            }
        },

        // 更新或添加任务到 taskList
        updateTaskInList({commit, state}, {taskData, taskId}) {
            const existingTaskIndex = state.taskList.findIndex(t => t.taskId === taskId);
            // 转换任务数据格式
            const transformedTask = transformTaskData([taskData])[0];
            if (existingTaskIndex !== -1) {
                // 找到了，更新现有任务
                const oldTask = state.taskList[existingTaskIndex];
                commit('UPDATE_TASK', {
                    taskId,
                    updates: {
                        ...transformedTask,
                        showDateLabel: oldTask.showDateLabel,
                        dateLabel: oldTask.dateLabel
                    }
                });
                console.log('更新任务:', taskId);
            }
            else {
                // 没找到，添加到列表开头
                commit('ADD_TASK', transformedTask);
                console.log('添加新任务到列表:', taskId);
            }
        },

        async cancelTask({commit, state, dispatch}, {model, taskId}) {
            // 如果传入了 taskId，使用传入的；否则查找生成中的主任务（AGENT_IMAGE_GENERATE）
            let currentTaskId = taskId;
            if (!currentTaskId) {
                const generatingTask = state.taskList.find(
                    t => t.status === 1 && t.imageGenerateType === 'AGENT_IMAGE_GENERATE'
                );
                if (!generatingTask) {
                    console.warn('没有找到生成中的任务');
                    return;
                }
                currentTaskId = generatingTask.taskId;
            }
            console.log('准备取消任务:', currentTaskId);
            // 调用后端接口取消任务
            try {
                await model.imageCreate.cancelImageAgentTask({taskId: currentTaskId});
                console.log('任务已取消:', currentTaskId);
                // 停止轮询
                dispatch('stopPolling', {taskId: currentTaskId});
                // 取消成功后，更新 taskList 中对应任务的状态为5（已取消）
                commit('UPDATE_TASK', {
                    taskId: currentTaskId,
                    updates: {
                        taskStatus: 5,
                        status: 5
                    }
                });
            }
            catch (error) {
                console.error('取消任务失败:', error);
                throw error;
            }
            // 注意：不主动清空 taskId/generateStatus，通过 getter 自动推导
        },

        clearInput({commit}) {
            commit('UPDATE_COMMON_STATE', {key: 'prompt', value: ''});
            commit('UPDATE_COMMON_STATE', {key: 'refImageList', value: []});
            commit('UPDATE_COMMON_STATE', {key: 'landPageUrl', value: ''});
        },

        // 更新单个状态
        updateState({commit}, {key, value}) {
            commit('UPDATE_COMMON_STATE', {key, value});
        },

        // 更新参数
        updateParams({commit, state}, {key, value}) {
            const params = {...state.params, [key]: value};
            commit('UPDATE_COMMON_STATE', {key: 'params', value: params});
        },

        // 打开详情弹窗
        openDetailDialog({commit, state}, {taskId, recordId}) {
            // 从 taskList 中查找完整的任务和记录信息
            const task = state.taskList.find(t => t.taskId === taskId);
            const record = task?.records?.find(r => r.recordId === recordId);
            commit('UPDATE_MULTIPLE_STATES', [
                {key: 'detailDialogOpen', value: true},
                {key: 'detailTaskInfo', value: task || null},
                {key: 'detailRecordInfo', value: record || null}
            ]);
        },

        // 关闭详情弹窗
        closeDetailDialog({commit, state}) {
            // 关闭时重置 modelParams
            const resetParams = {...defaultInputParams};
            commit('UPDATE_MULTIPLE_STATES', [
                {key: 'detailDialogOpen', value: false},
                {key: 'modelParams', value: {...state.modelParams, ...resetParams}},
                {key: 'detailTaskInfo', value: null},
                {key: 'detailRecordInfo', value: null}
            ]);
        },

        // 更新详情弹窗的当前记录
        updateDetailRecord({commit, state}, recordId) {
            // 从当前任务中查找新的 record 信息
            const record = state.detailTaskInfo?.records?.find(r => r.recordId === recordId);
            commit('UPDATE_COMMON_STATE', {
                key: 'detailRecordInfo',
                value: record || null
            });
        },

        // 更新详情弹窗的当前任务
        updateDetailTask({commit, state}, {taskId, recordId}) {
            // 从 taskList 中查找完整的任务和记录信息
            const task = state.taskList.find(t => t.taskId === taskId);
            const record = task?.records?.find(r => r.recordId === recordId);
            commit('UPDATE_MULTIPLE_STATES', [
                {key: 'detailTaskInfo', value: task || null},
                {key: 'detailRecordInfo', value: record || null}
            ]);
        },

        // 轮询单个抠图任务
        async pollCutoutTask({commit, state}, {model, taskId, tempTaskId}) {
            const maxRetries = POLLING_CONFIG.MAX_CUTOUT_RETRIES;
            let retries = 0;

            return new Promise((resolve, reject) => {
                const pollInterval = setInterval(async() => {
                    try {
                        retries++;
                        const historyList = await model.imageCreate.getHistory({
                            taskIds: [String(taskId)],
                            pageNo: 1,
                            pageSize: 1
                        });

                        if (historyList.listData?.length > 0) {
                            const taskData = historyList.listData[0];

                            // ✅ 关键：用轮询结果替代占位任务信息
                            const transformedTask = transformTaskData([taskData])[0];

                            // 找到该任务在 taskList 中的位置（用 tempTaskId 找占位卡片）
                            const tempTaskIndex = tempTaskId
                                ? state.taskList.findIndex(t => t.taskId === tempTaskId)
                                : -1;
                            const taskIndex = tempTaskIndex !== -1
                                ? tempTaskIndex
                                : state.taskList.findIndex(t => t.taskId === taskId);

                            if (taskIndex !== -1) {
                                // 完整替代占位任务（保留日期标签字段不变）
                                const oldTask = state.taskList[taskIndex];
                                commit('UPDATE_TASK', {
                                    taskId: oldTask.taskId, // 用旧 taskId 查找
                                    updates: {
                                        ...transformedTask,
                                        showDateLabel: oldTask.showDateLabel,
                                        dateLabel: oldTask.dateLabel
                                    }
                                });
                            }

                            // 检查是否完成
                            if (taskData.taskStatus !== 1) {
                                clearInterval(pollInterval);
                                resolve(taskData);
                            }
                        }

                        if (retries >= maxRetries) {
                            clearInterval(pollInterval);
                            reject(new Error('抠图任务轮询超时'));
                        }
                    }
                    catch (error) {
                        clearInterval(pollInterval);
                        reject(error);
                    }
                }, POLLING_CONFIG.CUTOUT_TASK_INTERVAL);
            });
        },

        // 添加参考图片
        addRefImage({commit, state}, {record}) {
            if (!record || !record.url) {
                throw new Error('图片URL缺失，无法添加');
            }

            const currentRefImages = state.params?.refImageList || [];
            const MAX_REF_IMAGES = 5;

            if (currentRefImages.length >= MAX_REF_IMAGES) {
                throw new Error('参考图已达上限');
            }

            // 构建新的refImageList，保持现有的标签序号
            const newRefImages = [
                ...currentRefImages,
                {
                    url: record.url,
                    label: `图片${currentRefImages.length + 1}`
                }
            ];

            // 更新Vuex store
            commit('UPDATE_COMMON_STATE', {
                key: 'params',
                value: {...state.params, refImageList: newRefImages}
            });
        },

        // 生视频：跳转到视频生成页面
        goToVideoGeneration(_, {record, router}) {
            if (!record || !record.url || !record.recordId) {
                throw new Error('图片信息缺失，无法生视频');
            }

            router.push({
                name: 'SceneryImg2VideoV2',
                params: {
                    from: 'imageAgent',
                    imageUrl: record.url,
                    imageName: `image_${record.recordId}`,
                    aimTab: 'image',
                    isGoback: true
                }
            });
        },
        goToImageEditor(_, {record, router, buildEditorJsonFn}) {
            if (!record || !record.url) {
                throw new Error('图片信息缺失，无法编辑');
            }

            const editorJson = JSON.stringify(buildEditorJsonFn(record.url, record.width, record.height));
            router.push({
                name: 'newImageEditorV2',
                params: {
                    editorJson,
                    beforeJumpName: 'imageAgentV2'
                }
            });
        }
    },
    getters: {
        // 从 taskList 推导出当前生成中的任务（仅限 AGENT_IMAGE_GENERATE 类型）
        generatingTask: state => {
            return state?.taskList?.find(
                t => t.status === 1 && t.imageGenerateType === 'AGENT_IMAGE_GENERATE'
            );
        },
        // 推导出 generateStatus：idle / submitting / generating
        generateStatus: (state, getters) => {
            // 如果正在提交，返回 submitting
            if (state._isSubmitting) {
                return 'submitting';
            }
            // 如果有生成中的任务，返回 generating
            const task = getters.generatingTask;
            if (task) {
                return 'generating';
            }
            // 否则返回 idle
            return 'idle';
        },
        // 推导出 taskId
        taskId: (state, getters) => {
            const task = getters.generatingTask;
            return task?.taskId || '';
        },
        pendingTasks: state => {
            return state.taskList.filter(t => t.status === TaskStatus.GENERATING);
        },
        getTaskByTaskType: state => taskType => {
            return state.taskList.filter(t => t.taskType === taskType);
        }
    }
};


/**
 * 重新计算 taskList 的日期标签（支持反转列表）
 * @param {Array} taskList - 任务列表（逆序：[最新, ..., 最旧]）
 * @param {Number} startIndex - 开始索引（默认 0，全量计算）
 * @param {Number} endIndex - 结束索引，不包含该位置（默认 taskList.length）
 *
 * 反转列表日期标签规则：
 *   - 比较"当前任务"和"下一个任务"的日期
 *   - 如果日期不同或是最后一个任务，就显示日期标签
 *   - 这样标签会出现在每个日期段的末尾（视觉上最老的位置），符合聊天应用习惯
 */
function recalcDateLabels(taskList, startIndex = 0, endIndex = null) {
    if (!taskList?.length) {
        return;
    }

    const end = endIndex ?? taskList.length;

    for (let i = startIndex; i < end; i++) {
        const task = taskList[i];
        const queryTime = task.queryTime;
        const dateLabel = formatDateLabel(queryTime);

        // 向后比较：与下一个任务比较，判断日期是否改变
        let showDateLabel = !taskList[i + 1];
        if (taskList[i + 1] && queryTime) {
            const nextQueryTime = taskList[i + 1].queryTime;
            if (nextQueryTime) {
                const currentDate = new Date(queryTime);
                const nextDate = new Date(nextQueryTime);
                // 如果日期不同，显示标签
                showDateLabel = !isSameDay(currentDate, nextDate);
            }
        }

        // 只在标签变化时更新
        if (task.showDateLabel !== showDateLabel || task.dateLabel !== dateLabel) {
            task.showDateLabel = showDateLabel;
            task.dateLabel = dateLabel;
        }
    }
}

/**
 * 将时间戳转换为后端格式的时间字符串
 * @param {Number} timestamp - 时间戳
 * @returns {String} 格式化后的时间字符串 "YYYY-MM-DD HH:mm:ss"
 */
function formatTimeString(timestamp) {
    return format(timestamp, 'yyyy-MM-dd HH:mm:ss');
}

/**
 * 格式化日期标签
 * @param {Number} queryTime - 时间戳
 * @returns {String} 格式化后的日期标签
 */
function formatDateLabel(queryTime) {
    if (!queryTime) {
        return '';
    }
    const date = new Date(queryTime);
    const now = new Date();

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    // 判断是否是今天
    const isToday = year === now.getFullYear()
        && month === (now.getMonth() + 1)
        && day === now.getDate();

    if (isToday) {
        return '今天';
    }

    // 判断是否是昨天
    if (isYesterday(date)) {
        return '昨天';
    }

    return `${year}年${month}月${day}日`;
}

/**
 * 转换任务数据为组件所需格式
 * @param {Array} listData - 原始任务列表数据
 * @returns {Array} 转换后的任务列表
 */
export function transformTaskData(listData) {
    // 接口返回 newest→oldest，直接使用，不再反转
    const result = [];

    for (let i = 0; i < listData.length; i++) {
        const item = listData[i];

        // 第一步：一次解析 ext JSON 字符串，后续所有函数直接使用
        let extData = {};
        try {
            extData = item.ext ? JSON.parse(item.ext) : {};
        }
        catch (e) {
            console.warn('Invalid ext JSON:', item.ext);
        }

        // 第二步：直接构建数据对象（避免深度 copy 和重复解析）
        const baseData = {
            ...item,
            status: item.taskStatus,
            prompt: extData.params?.prompt || ''
        };

        // 使用 queryTime 字段判断日期
        const queryTime = item.queryTime;
        const dateLabel = formatDateLabel(queryTime);

        // 向后比较：与下一条任务比较，判断日期是否改变
        let showDateLabel = !listData[i + 1];
        if (listData[i + 1]) {
            const nextQueryTime = listData[i + 1].queryTime;
            if (nextQueryTime) {
                const currentDate = new Date(queryTime);
                const nextDate = new Date(nextQueryTime);
                // 如果日期不同，或者是最后一条，显示标签
                showDateLabel = !isSameDay(currentDate, nextDate);
            }
        }

        // 为每个 record 初始化 checked 状态（用于批量选择）
        const recordsWithChecked = (baseData.records || []).map(record => ({
            ...record,
            checked: false
        }));

        // 扩展 imageAgent 需要的额外字段
        const referenceImages = extractReferenceImages(baseData, extData);

        result.push({
            ...item,
            ...baseData,
            records: recordsWithChecked,
            // 传递预解析的 extData，供后续函数使用，避免重复解析
            extData,
            // 兼容新组件使用的字段
            referenceImages,
            errorMsg: item.errorMsg || '',
            // 日期标记字段（已反转，直接使用）
            showDateLabel,
            dateLabel,
            // 虚拟滚动高度计算（用于 @tanstack/virtual-core estimateSize）
            size: calculateCardSize({
                imageGenerateType: baseData.imageGenerateType,
                records: recordsWithChecked,
                showDateLabel,
                status: baseData.status,
                extData,
                task: {
                    prompt: baseData.prompt,
                    referenceImages,
                    agentContent: baseData.agentContent
                }
            })
        });
    }

    return result;
}

/**
 * 从任务数据中提取参考图信息
 * @param {Object} item - 原始任务数据
 * @param {Object} extData - 预解析的扩展数据
 * @returns {Array} 参考图列表
 */
function extractReferenceImages(item, extData = {}) {
    const referenceImages = [];

    // 优先级 1:CardGenerate 新格式 - ext.params.refImageList
    if (extData.params?.refImageList?.length > 0) {
        referenceImages.push(...extData.params.refImageList.map(img => ({
            url: img.url || img,
            label: img.label || '参考图', // 使用 label 而不是 name
            name: img.label || '参考图', // 保留 name 作为兼容
            type: 'image'
        })));
    }

    // 优先级 2：landPageUrl - 作为特殊卡片展示
    if (extData.params?.landPageUrl) {
        referenceImages.push({
            url: extData.params.landPageUrl,
            label: '落地页', // 使用 label 而不是 name
            name: '落地页', // 保留 name 作为兼容
            type: 'landPage'
        });
    }

    return referenceImages;
}

/**
 * 构建提交参数
 * @param {Object} params - 输入参数对象，包含提示词、参考图列表、图片尺寸等信息
 * @returns {Object} 提交参数对象
 */
function buildSubmitParams(params) {
    // 根据 resolution 和 ratio 从 imageFormatConfig 获取宽高
    const resolution = params.resolution || '2k';
    const ratio = params.ratio || '16:9';
    const dimensions = imageFormatConfig[resolution]?.[ratio] || {width: 2848, height: 1600};
    const width = dimensions.width;
    const height = dimensions.height;
    params.width = width;
    params.height = height;

    return {
        prompt: params.prompt,
        refImageList: (params.refImageList || []).map(item => item.url),
        ratio: ratio,
        width,
        height,
        resolution: resolution,
        targetCount: params.targetCount,
        landPageUrl: params.landPageUrl || '',
        ext: JSON.stringify({params})
    };
}

/**
 * 构建参考图片列表，包含参考图和落地页信息
 * @param {Object} params - 输入参数对象，包含参考图列表和落地页URL
 * @returns {Array} 参考图片列表
 */
function buildReferenceImages(params) {
    const referenceImages = [];
    // 参考图片
    if (params.refImageList?.length > 0) {
        referenceImages.push(...params.refImageList.map(img => ({
            url: img.url,
            label: img.label || '参考图', // 使用 label 而不是 name
            name: img.label || '参考图', // 保留 name 作为兼容
            type: 'image'
        })));
    }
    // 落地页
    if (params.landPageUrl) {
        referenceImages.push({
            url: params.landPageUrl,
            label: '落地页', // 使用 label 而不是 name
            name: '落地页', // 保留 name 作为兼容
            type: 'landPage'
        });
    }
    return referenceImages;
}

/**
 * 创建新任务对象
 * @param {String} taskId - 任务ID
 * @param {Object} params - 输入参数对象
 * @param {Array} referenceImages - 参考图片列表
 * @returns {Object} 新任务对象
 */
function createNewTask(taskId, params, referenceImages) {
    const now = Date.now();
    const nowString = formatTimeString(now); // 转换为后端格式的时间字符串
    const dateLabel = formatDateLabel(now);
    const extData = {params};
    const newTask = {
        taskId,
        imageGenerateType: 'AGENT_IMAGE_GENERATE',
        status: 1,
        taskStatus: 1,
        records: [],
        prompt: params.prompt,
        queryTime: nowString,
        referenceImages,
        extData,
        ext: JSON.stringify({params}),
        errorMsg: '',
        showDateLabel: true,
        dateLabel,
        size: calculateCardSize({
            imageGenerateType: 'AGENT_IMAGE_GENERATE',
            records: [],
            showDateLabel: true,
            status: 1,
            extData,
                task: {
                    prompt: params.prompt,
                    referenceImages,
                    agentContent: null
                }
            })
    };
    return newTask;
}
